// Ported from LC2's UnitsWithConcessions.js (commons/components/HomeTabs/Performance/
// Metrics/) — same `getUnitsWithConcessions` fetch (keyed off `account_sfid`, which is
// present on every account/community/campaign route — see routes/AppRoutes.js), same
// permission-based filtering (Stake admin/Administrator sees all communities the dashboard
// currently scopes to, everyone else only their `propertyPermissions`), same month-grouping
// of the response into main rows (exits/concessions/stake+ offer counts) and per-building
// comparison rows.
// SIMPLIFIED:
// - LC2 rendered both tables with MUI X DataGridPro, including a DataGrid "detail panel"
//   expand row for the per-building comparison table. DataGridPro isn't a dependency of this
//   app; this is rebuilt as a plain MUI Table with a manual expand/collapse per row (a Set of
//   expanded month ids), same idea as LC2's `expandedRowsSet` state, just rendered as a
//   nested <Collapse> row instead of a DataGrid detail panel. Search/export/pagination chrome
//   is dropped (these tables are typically well under a page of rows).
// - The by-building concessions-percent chart (`D3GroupedBarChart`, only shown at the
//   community level) used d3 with an overlaid exits line. d3 isn't a dependency of this app
//   (see the task's charting-library rule) — rebuilt as a plain CSS grouped-bar column per
//   month (bar height = % of annual rent, same Stake+/Traditional color coding) with the
//   monthly "pending/signed leases" count shown as a small badge above each column instead of
//   an overlaid line — same underlying numbers, simpler rendering.
import React, {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import dayjs from 'dayjs';

import {Box, Chip, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';

import {tokens} from '../../../themes/ThemeCustomization';
import {getIsStakeAdmin, getPropertyPermissions, getTeamMemberRole} from '../../../app/store/reducers/session';
import {getDashboardCommunities} from '../../../app/store/reducers/dashboard';
import {useGetUnitsWithConcessionsQuery} from '../../../features/coreApi/metricsSlice';
import LoadingBox from '../../../commons/components/LoadingBox';

const STAKE_COLOR = '#149CE0';
const TRADITIONAL_COLOR = '#FEE3A0';

const sortDayjsAsc = (a, b) => a.valueOf() - b.valueOf();

// Plain-CSS replacement for LC2's D3GroupedBarChart — see file header comment.
const ConcessionsGraph = ({data}) => {
    const monthlyData = useMemo(() => {
        const months = {};

        for (const key in data) {
            const propertyData = data[key];
            for (const month in propertyData) {
                const monthData = propertyData[month];
                const formattedMonth = dayjs(month).format('YYYY-MM');
                if (!months[formattedMonth]) {
                    months[formattedMonth] = {label: dayjs(month).format("MMM'YY"), dayjsDate: dayjs(month), exits: 0, bars: []};
                }
                months[formattedMonth].exits += monthData.exits || 0;

                (monthData.concessions || []).forEach((c) => {
                    months[formattedMonth].bars.push({value: c?.concession_percent?.total_percent || 0, color: TRADITIONAL_COLOR, text: c?.concessions});
                });
                (monthData.stake_offers || []).forEach((c) => {
                    months[formattedMonth].bars.push({value: c?.concession_percent?.total_percent || 0, color: STAKE_COLOR, text: c?.concessions});
                });
            }
        }

        return Object.values(months).sort((a, b) => sortDayjsAsc(a.dayjsDate, b.dayjsDate));
    }, [data]);

    const maxValue = useMemo(
        () => Math.max(1, ...monthlyData.flatMap((m) => m.bars.map((b) => b.value))),
        [monthlyData]
    );

    if (monthlyData.length === 0) return null;

    return (
        <Box sx={{width: '100%', p: 2}}>
            <Box sx={{display: 'flex', alignItems: 'flex-end', gap: 2, height: 180, overflowX: 'auto', px: 1}}>
                {monthlyData.map((month) => (
                    <Stack key={month.label} alignItems="center" sx={{flex: '0 0 auto', minWidth: 48, height: '100%'}} justifyContent="flex-end">
                        {month.exits > 0 && (
                            <Chip
                                label={month.exits}
                                size="small"
                                sx={{height: 18, mb: 0.5, backgroundColor: tokens.color.neutral100, '& .MuiChip-label': {px: 0.75, fontSize: 11}}}
                            />
                        )}
                        <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{height: 130}}>
                            {month.bars.map((bar, idx) => (
                                <Box
                                    key={idx}
                                    title={`${bar.text} — ${bar.value}%`}
                                    sx={{
                                        width: 8,
                                        height: Math.max(2, (bar.value / maxValue) * 130),
                                        backgroundColor: bar.color,
                                        borderRadius: '2px 2px 0 0'
                                    }}
                                />
                            ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{mt: 0.5}}>
                            {month.label}
                        </Typography>
                    </Stack>
                ))}
            </Box>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{pt: 2}}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 12, height: 12, borderRadius: '2px', backgroundColor: TRADITIONAL_COLOR}} />
                    <Typography variant="caption">Traditional concessions</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{width: 12, height: 12, borderRadius: '2px', backgroundColor: STAKE_COLOR}} />
                    <Typography variant="caption">Stake+ Offers</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Chip label="#" size="small" sx={{height: 14, '& .MuiChip-label': {px: 0.5, fontSize: 10}}} />
                    <Typography variant="caption">Pending/signed leases</Typography>
                </Stack>
            </Stack>
        </Box>
    );
};

const ComparisonTable = ({row}) => {
    const rows = useMemo(() => {
        const allRows = [];

        for (const c of row.concessions) {
            allRows.push({
                id: c?.id,
                building_name: c?.name,
                type: c?.type,
                concessions: c?.concessions,
                total_percent: c?.concession_percent?.total_percent,
                from_date: c?.from_date,
                to_date: c?.to_date
            });
        }
        for (const c of row.stake_offers) {
            allRows.push({
                id: c?.id,
                building_name: c?.name,
                type: c?.type,
                concessions: c?.concessions,
                total_percent: c?.concession_percent?.total_percent,
                from_date: c?.from_date,
                to_date: c?.to_date
            });
        }

        return allRows.sort((a, b) => String(a.building_name ?? '').localeCompare(String(b.building_name ?? '')));
    }, [row]);

    return (
        <Box sx={{width: '100%', backgroundColor: tokens.color.neutral50, p: 2}}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{fontWeight: 500}}>Property</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Type</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Details</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Est. % of annual rent</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Active from</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Active to</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, idx) => (
                        <TableRow key={r.id || `${r.building_name}-${idx}`}>
                            <TableCell>{r.building_name}</TableCell>
                            <TableCell>{r.type}</TableCell>
                            <TableCell>{r.concessions}</TableCell>
                            <TableCell>{r.total_percent != null ? `${r.total_percent}%` : '-'}</TableCell>
                            <TableCell>{r.from_date ? dayjs(r.from_date).format('MM/DD/YYYY') : '-'}</TableCell>
                            <TableCell>{r.to_date ? dayjs(r.to_date).format('MM/DD/YYYY') : '-'}</TableCell>
                        </TableRow>
                    ))}
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={6} sx={{textAlign: 'center', color: 'text.secondary'}}>
                                No comparable concessions found
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
};

const UnitsWithConcessionsTable = ({allData}) => {
    const params = useParams();
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isAdministrator = teamMemberRole === 'Administrator';
    const propertyPermissions = useSelector(getPropertyPermissions);
    const communities = useSelector(getDashboardCommunities);
    const [expandedRows, setExpandedRows] = useState(new Set());

    const data = useMemo(() => {
        const obj = {};
        for (const key in allData) {
            if (isStakeAdmin || isAdministrator) {
                const cSfids = params?.community_sfid ? [params.community_sfid] : communities.map((c) => c.sfid);
                if (cSfids.includes(key)) obj[key] = allData[key];
            } else if (propertyPermissions.includes(key)) {
                obj[key] = allData[key];
            }
        }
        return obj;
    }, [allData, isStakeAdmin, isAdministrator, propertyPermissions, communities, params]);

    const mainRows = useMemo(() => {
        const monthsObj = {};

        for (const sfid in data) {
            const monthData = data[sfid];
            for (const month in monthData) {
                const concessions = (monthData[month]?.concessions || []).map((c) => ({...c, type: 'Traditional Concession'}));
                const stakeOffers = (monthData[month]?.stake_offers || []).map((c) => ({...c, type: 'Stake+ Offer'}));
                const obj = {
                    month: dayjs(month).format('MMMM YYYY'),
                    concessions,
                    stake_offers: stakeOffers,
                    exits: monthData[month]?.exits || 0,
                    concessions_count: monthData[month]?.concessions_count || 0,
                    stake_offers_count: monthData[month]?.stake_offers_count || 0,
                    id: `${sfid}_${month}`
                };
                if (!monthsObj[month]) monthsObj[month] = [obj];
                else monthsObj[month].push(obj);
            }
        }

        const rows = [];
        for (const month in monthsObj) {
            rows.push({
                month: dayjs(month),
                concessions_count: monthsObj[month].reduce((acc, curr) => acc + curr.concessions_count, 0),
                stake_offers_count: monthsObj[month].reduce((acc, curr) => acc + curr.stake_offers_count, 0),
                exits: monthsObj[month].reduce((acc, curr) => acc + curr.exits, 0),
                concessions: monthsObj[month].flatMap((c) => c.concessions),
                stake_offers: monthsObj[month].flatMap((c) => c.stake_offers),
                id: month
            });
        }

        return rows.sort((a, b) => b.month.valueOf() - a.month.valueOf());
    }, [data]);

    const toggleExpanded = (id) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <Stack direction="column">
            <Stack direction="column" sx={{width: '100%', p: 2}}>
                <Typography variant="h5" fontWeight="fontWeightMedium">
                    Concession Performance [BETA]
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Visualize how your concession offers impacted the number of pending/signed leases.
                </Typography>
            </Stack>
            {params?.community_sfid && <ConcessionsGraph data={data} />}
            <Table>
                <TableHead>
                    <TableRow sx={{backgroundColor: tokens.color.neutral50}}>
                        <TableCell sx={{fontWeight: 500}} />
                        <TableCell sx={{fontWeight: 500}}>Month</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Pending/signed leases</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Stake+ Offers</TableCell>
                        <TableCell sx={{fontWeight: 500}}>Traditional Concessions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {mainRows.map((row) => (
                        <React.Fragment key={row.id}>
                            <TableRow>
                                <TableCell sx={{width: 40}}>
                                    <IconButton size="small" onClick={() => toggleExpanded(row.id)}>
                                        {expandedRows.has(row.id) ? <ExpandLessOutlined fontSize="small" /> : <ExpandMoreOutlined fontSize="small" />}
                                    </IconButton>
                                </TableCell>
                                <TableCell>{row.month.format('MMMM YYYY')}</TableCell>
                                <TableCell>{row.exits}</TableCell>
                                <TableCell>{row.stake_offers_count}</TableCell>
                                <TableCell>{row.concessions_count}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={5} sx={{p: 0, border: expandedRows.has(row.id) ? undefined : 'none'}}>
                                    <Collapse in={expandedRows.has(row.id)}>
                                        <ComparisonTable row={row} />
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    ))}
                    {mainRows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} sx={{textAlign: 'center', color: 'text.secondary', py: 4}}>
                                There is no lease signings with concessions data
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

const UnitsWithConcessions = () => {
    const params = useParams();
    const {data, isLoading, isFetching, isError} = useGetUnitsWithConcessionsQuery(params?.account_sfid, {
        skip: !params?.account_sfid
    });

    let content;
    if (isLoading || isFetching) {
        content = <LoadingBox />;
    } else if (isError) {
        content = (
            <Typography variant="body2" color="error.main" sx={{textAlign: 'center', py: 4}}>
                Error fetching unit with concessions data
            </Typography>
        );
    } else {
        content = <UnitsWithConcessionsTable allData={data} />;
    }

    return (
        <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white, overflow: 'hidden'}}>
            {content}
        </Box>
    );
};

export default UnitsWithConcessions;
